import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle,
  Cancel,
  Refresh,
} from '@mui/icons-material';
import api from '@/services/ApiService';
import { Box, Typography, IconButton } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { useEffect } from 'react';
import { useState, useRef } from 'react';
import proxy from '@/services/ProxyService';
import { toast } from 'react-toastify';
import { useRouteLoaderData } from 'react-router-dom';

export default function MassActionDialog({ open, onClose }) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [foundTitle, setFoundTitle] = useState('');
  const [options, setOptions] = useState([]);
  const typingTimeoutRef = useRef(null);
  const abortRef = useRef(null);
  const [step, setStep] = useState(0);
  const [parsedNumbers, setParsedNumbers] = useState([]);

  const [items, setItems] = useState([]);
  const [weclappArticleId, setWeclappArticleId] = useState('');

  const authLoader = useRouteLoaderData && useRouteLoaderData('auth');
  const deploymentContracts = authLoader?.fixedDeploymentConfigs?.contracts;
  const weclappInterface = deploymentContracts?.interfaces?.weclapp;
  const contractShares = deploymentContracts?.contractShares || [];

  const parseInput = (text) => {
    if (!text) return [];
    const matches = text.match(/\b[vV]?\d+\b/g) || [];
    const unique = Array.from(new Set(matches.map((m) => m.toLowerCase())));
    return unique;
  };

  const normalizeDate = (val) => {
    if (!val && val !== 0) return undefined;
    if (typeof val === 'string') return val;
    if (val instanceof Date) return val.toISOString();
    try {
      const d = new Date(val);
      if (!isNaN(d.getTime())) return d.toISOString();
    } catch (e) {}
    return String(val);
  };

  useEffect(() => {
    const nums = parseInput(query);
    setParsedNumbers(nums);
    setItems((prev) => {
      const byNum = {};
      prev.forEach((p) => (byNum[p.number] = p));
      return nums.map(
        (n) =>
          byNum[n] || {
            number: n,
            weclappStatus: 'idle',
            datasourceStatus: 'idle',
            createStatus: 'idle',
          },
      );
    });
  }, [query]);

  const delay = (ms) => new Promise((r) => setTimeout(r, ms));

  const runWeclappLookupFor = async (index) => {
    setItems((s) =>
      s.map((it, i) =>
        i === index ? { ...it, weclappStatus: 'loading' } : it,
      ),
    );
    const num = items[index]?.number;
    try {
      const { data } = await proxy.get(
        `${weclappInterface}/contract?contractNumber-ilike=%25${encodeURIComponent(num)}%25`,
      );
      const found = data?.result?.[0];
      if (found) {
        setItems((s) =>
          s.map((it, i) =>
            i === index
              ? { ...it, weclappStatus: 'success', weclapp: found }
              : it,
          ),
        );
      } else {
        setItems((s) =>
          s.map((it, i) =>
            i === index ? { ...it, weclappStatus: 'error', weclapp: null } : it,
          ),
        );
      }
    } catch (err) {
      console.error(err);
      setItems((s) =>
        s.map((it, i) =>
          i === index ? { ...it, weclappStatus: 'error', weclapp: null } : it,
        ),
      );
    }
  };

  const runWeclappLookupAll = async () => {
    for (let i = 0; i < items.length; i++) {
      await runWeclappLookupFor(i);
      await delay(50);
    }
  };

  const runDatasourceLookupFor = async (index) => {
    setItems((s) =>
      s.map((it, i) =>
        i === index ? { ...it, datasourceStatus: 'loading' } : it,
      ),
    );
    const contract = items[index]?.weclapp;
    const partyId = contract?.partyId;
    if (!partyId) {
      setItems((s) =>
        s.map((it, i) =>
          i === index ? { ...it, datasourceStatus: 'error' } : it,
        ),
      );
      return;
    }
    try {
      const { data } = await api.get(
        `/datasource?source=weclapp&sourceId=${partyId}`,
      );
      const found = data?.result?.[0];
      const { data: party } = await api.get(`/party/${found?.recordId}`);
      if (found) {
        setItems((s) =>
          s.map((it, i) =>
            i === index
              ? {
                  ...it,
                  datasourceStatus: 'success',
                  datasourceRecordId: found.recordId,
                  party: party,
                }
              : it,
          ),
        );
      } else {
        setItems((s) =>
          s.map((it, i) =>
            i === index ? { ...it, datasourceStatus: 'error' } : it,
          ),
        );
      }
    } catch (err) {
      console.error(err);
      setItems((s) =>
        s.map((it, i) =>
          i === index ? { ...it, datasourceStatus: 'error' } : it,
        ),
      );
    }
  };

  const runDatasourceLookupAll = async () => {
    for (let i = 0; i < items.length; i++) {
      if (items[i].weclappStatus === 'success') {
        await runDatasourceLookupFor(i);
        await delay(20);
      }
    }
  };

  const runCreateContractFor = async (index) => {
    setItems((s) =>
      s.map((it, i) => (i === index ? { ...it, createStatus: 'loading' } : it)),
    );
    const it = items[index];
    const contractFromWeclapp = it.weclapp;
    const contractItems = [];
    console.log(contractFromWeclapp);

    if (
      contractFromWeclapp?.contractItems &&
      contractFromWeclapp.contractItems.length > 0
    ) {
      for (const ci of contractFromWeclapp.contractItems) {
        contractItems.push({
          uuid: uuidv4(),
          type: 'STATIC',
          articleType: ci.articleType || 'BASIC',
          articleId: ci.articleId || undefined,
          articleNumber: ci.articleNumber || undefined,
          quantity: Number(ci.quantity) || 0,
          title: ci.title || ci.name || '',
          discountPercentage: Number(ci.discountPercentage) || 0,
          description: String(ci.description || '')
            .replace(/<[^>]*>/g, '')
            .trim(),
          unitPrice:
            ci.unitPrice != null
              ? Number(ci.unitPrice)
              : ci.price != null
                ? Number(ci.price)
                : 0,
          unchangedUnitPrice:
            ci.unitPrice != null
              ? Number(ci.unitPrice)
              : ci.price != null
                ? Number(ci.price)
                : 0,
          pricingDate: contractFromWeclapp?.pricingDate
            ? new Date(contractFromWeclapp.pricingDate).toISOString()
            : null,
          startDate: ci?.createdDate
            ? new Date(ci.createdDate).toISOString()
            : null,
          endDate: contractFromWeclapp?.endDate
            ? new Date(contractFromWeclapp.endDate).toISOString()
            : null,
          cancellationNotification:
            contractFromWeclapp?.reminderType !== 'NO_REMINDER' ? true : false,
          cancellationNotificationQuantity:
            contractFromWeclapp?.latestCancellationWarningQuantity || undefined,
          cancellationNotificationUnit:
            contractFromWeclapp?.latestCancellationWarningUnit || undefined,
          extensionQuantity:
            contractFromWeclapp?.extensionQuantity || undefined,
          extensionUnit: contractFromWeclapp?.extensionUnit || undefined,
          cancellationPeriodQuantity:
            contractFromWeclapp?.cancellationPeriodQuantity || undefined,
          cancellationPeriodUnit:
            contractFromWeclapp?.cancellationPeriodUnit || undefined,
          lastCancellationNotificationDate: null,
          salesBillOfMaterialItems: [],
        });
      }
    } else {
      const articleIdToUse = weclappArticleId || null;
      contractItems.push({
        uuid: uuidv4(),
        type: 'STATIC',
        discountPercentage: 0,
        articleType: 'BASIC',
        articleId: articleIdToUse,
        articleNumber: undefined,
        quantity: 0,
        title: articleIdToUse
          ? `Imported article ${articleIdToUse}`
          : `Dummy position for ${it.number}`,
        description: `Dummy position created from Weclapp contract ${it.number}`,
        unitPrice: 0,
        unchangedUnitPrice: 0,
        pricingDate:
          contractFromWeclapp?.pricingDate ||
          contractFromWeclapp?.servicePeriodFrom ||
          null,
        pricingDate: contractFromWeclapp?.pricingDate
          ? new Date(contractFromWeclapp.pricingDate).toISOString()
          : null,
        startDate: contractFromWeclapp?.createdDate
          ? new Date(contractFromWeclapp.createdDate).toISOString()
          : null,
        endDate: contractFromWeclapp?.endDate
          ? new Date(contractFromWeclapp.endDate).toISOString()
          : null,
        cancellationNotification:
          contractFromWeclapp?.reminderType !== 'NO_REMINDER' ? true : false,
        cancellationNotificationQuantity:
          contractFromWeclapp?.latestCancellationWarningQuantity || undefined,
        cancellationNotificationUnit:
          contractFromWeclapp?.latestCancellationWarningUnit || undefined,
        extensionQuantity: contractFromWeclapp?.extensionQuantity || undefined,
        extensionUnit: contractFromWeclapp?.extensionUnit || undefined,
        cancellationPeriodQuantity:
          contractFromWeclapp?.cancellationPeriodQuantity || undefined,
        cancellationPeriodUnit:
          contractFromWeclapp?.cancellationPeriodUnit || undefined,
        lastCancellationNotificationDate: null,
        salesBillOfMaterialItems: [],
      });
    }

    const payload = {
      contractNumber: it.number,
      title:
        contractFromWeclapp?.title || contractFromWeclapp?.name || undefined,
      description:
        contractFromWeclapp?.description ||
        contractFromWeclapp?.commission ||
        undefined,
      ...(normalizeDate(
        contractFromWeclapp?.contractDate || contractFromWeclapp?.createdAt,
      )
        ? {
            contractDate: normalizeDate(
              contractFromWeclapp?.contractDate ||
                contractFromWeclapp?.createdAt,
            ),
          }
        : {}),
      billingFrequency: 'no_billing',
      partyId: it.datasourceRecordId || undefined,
      partyName:
        `${it.party?.partyType === 'PERSON' ? `${it.party?.firstName || ''} ${it.party?.lastName || ''}` : `${it.party?.company ? `${it.party.company}${it.party?.company2 ? ` ${it.party.company2}` : ''}` : ''}`}` ||
        undefined,
      contractItems,
      groupNames: contractFromWeclapp?.groupNames || [],
      sharedWith: [...(contractShares || [])],
    };
    try {
      const { data } = await api.post('/contract', payload);
      const createdId = data?.insertedId || data?._id || null;
      if (createdId) {
        setItems((s) =>
          s.map((x, i) =>
            i === index
              ? { ...x, createStatus: 'success', createResult: createdId }
              : x,
          ),
        );
      } else {
        setItems((s) =>
          s.map((x, i) => (i === index ? { ...x, createStatus: 'error' } : x)),
        );
      }
    } catch (err) {
      console.error(err);
      setItems((s) =>
        s.map((x, i) => (i === index ? { ...x, createStatus: 'error' } : x)),
      );
    }
  };

  const runCreateAll = async () => {
    for (let i = 0; i < items.length; i++) {
      if (items[i].datasourceStatus === 'success') {
        await runCreateContractFor(i);
        await delay(20);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Massenaktion</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1">Schritt {step + 1} von 4</Typography>
        </Box>

        {step === 0 && (
          <>
            <TextField
              label="Vertragsnummern (ein oder mehrere, mit/ohne 'v')"
              multiline
              minRows={4}
              fullWidth
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="textSecondary">
                {parsedNumbers.slice(0, 5).join(', ')}
                {parsedNumbers.length > 5
                  ? ` ... (${parsedNumbers.length} gefunden)`
                  : parsedNumbers.length === 0
                    ? ' (keine gefunden)'
                    : ''}
              </Typography>
            </Box>
          </>
        )}

        {step === 1 && (
          <>
            <Typography variant="subtitle2">Weclapp Suche</Typography>
            <Box sx={{ mt: 1 }}>
              <TextField
                label="ArticleId (optional, für Dummy-Position)"
                size="small"
                fullWidth
                value={weclappArticleId}
                onChange={(e) => setWeclappArticleId(e.target.value)}
              />
            </Box>
            <Box sx={{ mt: 1 }}>
              {items.length === 0 && (
                <Typography>Keine Nummern zum Suchen.</Typography>
              )}
              {items.map((it, idx) => (
                <Box
                  key={it.number}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Typography sx={{ width: 120 }}>{it.number}</Typography>
                  <Box sx={{ flex: 1 }}>
                    {it.weclappStatus === 'loading' && (
                      <CircularProgress size={18} />
                    )}
                    {it.weclappStatus === 'success' && (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <CheckCircle color="success" />
                        <Typography>
                          {it.weclapp?.title || it.weclapp?.name}
                        </Typography>
                      </Box>
                    )}
                    {it.weclappStatus === 'error' && (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Cancel color="error" />
                        <Typography>nicht gefunden</Typography>
                      </Box>
                    )}
                  </Box>
                  {it.weclappStatus === 'error' && (
                    <IconButton
                      size="small"
                      onClick={() => runWeclappLookupFor(idx)}
                    >
                      <Refresh />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </>
        )}

        {step === 2 && (
          <>
            <Typography variant="subtitle2">Datasources ermitteln</Typography>
            <Box sx={{ mt: 1 }}>
              {items.map((it, idx) => (
                <Box
                  key={it.number}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Typography sx={{ width: 120 }}>{it.number}</Typography>
                  <Box sx={{ flex: 1 }}>
                    {it.datasourceStatus === 'loading' && (
                      <CircularProgress size={18} />
                    )}
                    {it.datasourceStatus === 'success' && (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <CheckCircle color="success" />
                        <Typography>
                          {it.party?.partyType === 'PERSON'
                            ? `${it.party?.firstName || ''} ${it.party?.lastName || ''}`
                            : `${it.party?.company ? `${it.party.company}${it.party?.company2 ? ` ${it.party.company2}` : ''}` : ''}`}
                        </Typography>
                      </Box>
                    )}
                    {it.datasourceStatus === 'error' && (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Cancel color="error" />
                        <Typography>nicht zugeordnet</Typography>
                      </Box>
                    )}
                  </Box>
                  {it.datasourceStatus === 'error' && (
                    <IconButton
                      size="small"
                      onClick={() => runDatasourceLookupFor(idx)}
                    >
                      <Refresh />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </>
        )}

        {step === 3 && (
          <>
            <Typography variant="subtitle2">Verträge anlegen</Typography>
            <Box sx={{ mt: 1 }}>
              {items.map((it, idx) => (
                <Box
                  key={it.number}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Typography sx={{ width: 120 }}>{it.number}</Typography>
                  <Box sx={{ flex: 1 }}>
                    {it.createStatus === 'loading' && (
                      <CircularProgress size={18} />
                    )}
                    {it.createStatus === 'success' && (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <CheckCircle color="success" />
                        <Typography>id: {it.createResult}</Typography>
                      </Box>
                    )}
                    {it.createStatus === 'error' && (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Cancel color="error" />
                        <Typography>Fehler beim Anlegen</Typography>
                      </Box>
                    )}
                  </Box>
                  {it.createStatus === 'error' && (
                    <IconButton
                      size="small"
                      onClick={() => runCreateContractFor(idx)}
                    >
                      <Refresh />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Schließen
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          color="inherit"
          disabled={step === 0}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
        >
          Zurück
        </Button>
        {step === 0 && (
          <Button
            variant="contained"
            disabled={parsedNumbers.length === 0}
            onClick={() => setStep(1)}
          >
            Next
          </Button>
        )}
        {step === 1 && (
          <Button
            variant="contained"
            onClick={async () => {
              const hasSuccess = items.some(
                (i) => i.weclappStatus === 'success',
              );
              if (!hasSuccess) await runWeclappLookupAll();
              setStep(2);
            }}
            disabled={items.length === 0}
          >
            Next
          </Button>
        )}
        {step === 2 && (
          <Button
            variant="contained"
            onClick={async () => {
              const hasDatasource = items.some(
                (i) => i.datasourceStatus === 'success',
              );
              if (!hasDatasource) await runDatasourceLookupAll();
              setStep(3);
            }}
            disabled={!items.some((i) => i.weclappStatus === 'success')}
          >
            Next
          </Button>
        )}
        {step === 3 && (
          <Button
            variant="contained"
            onClick={async () => {
              const hasCreated = items.some(
                (i) => i.createStatus === 'success',
              );
              if (!hasCreated) await runCreateAll();
            }}
            disabled={!items.some((i) => i.datasourceStatus === 'success')}
          >
            Ausführen
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
